/* eslint-disable max-lines */
import { AnswersArticleFilterEditorPlatform } from '@wix/ambassador-cc-helpcenter-v1-related-document/types';
import { EditorType } from '@wix/ambassador-meta-site-search-web/types';
import { useHttpClient, useFedops } from '@wix/fe-essentials-standalone';
import DOMPurify from 'isomorphic-dompurify';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { pushUrl } from '../../components/InnerLink';
import {
  SearchQuestionBIParams,
  useSearchBarBI,
} from '../../components/SearchBar/bi';
import { SearchResults } from '../../components/SearchResults';
import {
  ANSWER_GENERATION_TIME_BUDGET,
  ELASTIC_SEARCH_LIMIT,
  FEDOPS,
  LOCALES,
} from '../../constants';
import { Context } from '../../context';
import useIsAnswerGenerationMediaMobileEnabled from '../../hooks/useIsAnswerGenerationMediaMobileEnabled';
import { useIsGenAnswer } from '../../hooks/useIsGenAnswer';
import { useScrollDelegation } from '../../hooks/useScrollDelegation';
import { useWordByWordAnimation } from '../../hooks/useWordByWordAnimation';
import { getPageSeo } from '../../services/answers';
import {
  ArticleSearchResult,
  ArticleSearchResultItem,
  GetWixServerSidePropsFunction,
  PageType,
  HttpStatusCode,
} from '../../types';
import { getCategoriesPaths } from '../../utils/categories';
import {
  streamGenerateAnswer,
  fetchRefinementQuestions,
} from '../../utils/generate-answer';
import { createLogger, LoggerNamespaces } from '../../utils/logger';
import { getKASearchResults } from '../../utils/pages/search/get-search-results';
import { metaSiteSearchEditorTypeConverter } from '../../utils/site-picker';
import { withServerSideProps } from '../../utils/with-server-side-props';
import { useStore } from '../../zustand/stores';

export const SEARCH_RESULTS_PAGE_SIZE = 12;

export type SearchPageProps = {
  searchResults: ArticleSearchResult;
  term: string;
  initCurrentPage: number;
  isContactSupport: boolean;
  biInputType?: string;
  editor?: AnswersArticleFilterEditorPlatform;
  previousPageType: PageType;
};

const itemIdKey = (term: string, page: number) => `${term}_${page}`;

const SearchPage: NextPage<SearchPageProps> = ({
  searchResults,
  term,
  initCurrentPage,
  isContactSupport,
  biInputType,
  editor,
  previousPageType,
}: SearchPageProps) => {
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree
  );
  const httpClient = useHttpClient();
  const fedopsApi = useFedops();
  const [currentPage, setCurrentPage] = useState(initCurrentPage);
  const [searchTerm, setSearchTerm] = useState(term);
  const [isContactSupportState, setIsContactSupportState] = useState(isContactSupport);
  const { searchQuestionBI } = useSearchBarBI();
  const {
    locale,
    query: { editorType },
  } = useRouter();
  const [currentSearchResults, setCurrentSearchResults] =
    useState<ArticleSearchResult>(searchResults);
  const [isLoading, setIsLoading] = useState(false);
  const [usedArticlesInfo, setUsedArticlesInfo] = useState<{
    [id: string]: string;
  }>({});
  const [isProhibited, setIsProhibited] = useState<boolean | null>(null);
  const [refinementQuestions, setRefinementQuestions] = useState<
    string[]
  >([]);
  const [mediaRefsMap, setMediaRefsMap] = useState<Record<string, string>>({});
  const isFedopsInteractionEndedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentStreamIdRef = useRef(0);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const {
    displayedText: generatedAnswer,
    isAnimating,
    appendText,
    setText,
    finishStreaming,
    reset: resetAnimation,
  } = useWordByWordAnimation();
  const context = useContext(Context);
  const { isMobile } = context;
  const { searchSessionId, setSearchSessionId } = useStore((state) => state.biSlice);
  const isGenAnswerEnabled = useIsGenAnswer();
  const isAnswerGenerationMediaMobileEnabled = useIsAnswerGenerationMediaMobileEnabled();

  useScrollDelegation(searchContainerRef, true);
  const categoryIds =
    (currentSearchResults.items &&
      currentSearchResults?.items.map(
        (searchResult) => searchResult.categoryId || ''
      )) ||
    [];

  const initNumberOfResults = currentSearchResults.items
    ? currentSearchResults.items.length
    : 0;
  const allPaths = getCategoriesPaths(categoriesTree, categoryIds);
  const shouldGenerateAnswer =
    isGenAnswerEnabled && currentPage === 1 && !isContactSupport;

  useEffect(() => {
    if (searchResults) {
      setCurrentPage(initCurrentPage);
      setCurrentSearchResults(searchResults);
    }
  }, [searchResults, initCurrentPage, term]);

  useEffect(() => {
    setSearchTerm(term);
  }, [term]);

  const fetchSearchResults = useCallback(
    async (page: number, newTerm?: string) => {
      const { data } = await httpClient.get<ArticleSearchResult>(
        '/api/article/search',
        {
          params: {
            locale,
            text: newTerm || searchTerm,
            pageSize: SEARCH_RESULTS_PAGE_SIZE,
            page,
            editor,
          },
        }
      );
      return data;
    },
    [httpClient, locale, searchTerm, editor]
  );

  const sendSearchQuestionBI = useCallback(async (
    generatedAnswerText?: string | null,
    usedArticles?: string[],
    isProhibitedTerm?: boolean,
    mediaRefsMapParam?: Record<string, string>,
    searchQuestionBIParamsInput?: Partial<SearchQuestionBIParams>
  ) => {
    const newSearchSessionId = uuidv4();
    setSearchSessionId(newSearchSessionId);
    const searchQuestionBIParams: SearchQuestionBIParams = {
      results: currentSearchResults?.items as ArticleSearchResultItem[],
      pageType: previousPageType,
      inputText: searchTerm,
      searchMethod: currentSearchResults?.searchMethod,
      searchVersion: currentSearchResults?.searchVersion,
      inputType: biInputType,
      isContactSupport,
      isProhibited: isProhibitedTerm,
      isGenerateAnswerFailed:
        generatedAnswerText?.length === 0 && shouldGenerateAnswer,
      searchSessionId: newSearchSessionId,
      ...searchQuestionBIParamsInput,
    };

    const selectedSite = context.siteSelectorData?.selectedSite || undefined;
    void searchQuestionBI(
      searchQuestionBIParams,
      generatedAnswerText,
      usedArticles,
      selectedSite,
      mediaRefsMapParam
    );
  }, [
    currentSearchResults,
    previousPageType,
    searchTerm,
    biInputType,
    isContactSupport,
    shouldGenerateAnswer,
    searchSessionId,
    context.siteSelectorData?.selectedSite,
    searchQuestionBI
  ]);

  const generateAnswer = useCallback(async (
    newTerm: string,
    isForceGenerateAnswer?: boolean,
    resultsOverride?: ArticleSearchResult
  ): Promise<{ completed: boolean; aborted: boolean }> => {
    const effectiveResults = resultsOverride || searchResults;
    if (effectiveResults?.items?.length > 0 && shouldGenerateAnswer || isForceGenerateAnswer) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      resetAnimation();
      setUsedArticlesInfo({});
      setIsProhibited(null);
      setMediaRefsMap({});
      isFedopsInteractionEndedRef.current = false;

      currentStreamIdRef.current += 1;
      const thisStreamId = currentStreamIdRef.current;

      abortControllerRef.current = new AbortController();
      try {
        fedopsApi.interactionStarted(
          FEDOPS.INTERACTION_NAMES.ANSWER_GENERATION_STREAMED
        );

        fetchRefinementQuestions(newTerm, locale ?? LOCALES.EN, abortControllerRef.current)
          .then((questions) => setRefinementQuestions(questions ?? []))
          .catch(() => setRefinementQuestions([]));

        const agPromptId = (!isMobile || (isMobile && isAnswerGenerationMediaMobileEnabled))
          ? '59524de8-c751-4ca6-9de1-1b56e43ab509'
          : '2337872b-efa6-4d57-8125-a289967507f7';

        await streamGenerateAnswer(
          {
            searchTerm: newTerm || searchTerm,
            language: locale ?? LOCALES.EN,
            editorType,
            agPromptId,
          },
          ANSWER_GENERATION_TIME_BUDGET,
          (partial: any) => {
            if (thisStreamId !== currentStreamIdRef.current) {
              return;
            }

            if (
              typeof partial.generatedAnswerChunk === 'string' &&
              partial.generatedAnswerChunk.length > 0
            ) {
              if (!isFedopsInteractionEndedRef.current) {
                fedopsApi.interactionEnded(
                  FEDOPS.INTERACTION_NAMES.ANSWER_GENERATION_STREAMED,
                  {
                    customParams: {
                      source: FEDOPS.ANSWER_GENERATION_SOURCES.STREAMING,
                    },
                  }
                );
                isFedopsInteractionEndedRef.current = true;
              }
              appendText(partial.generatedAnswerChunk);
              setIsProhibited(false);
            } else if (partial.generatedAnswer !== null) {
              if (!isFedopsInteractionEndedRef.current) {
                fedopsApi.interactionEnded(
                  FEDOPS.INTERACTION_NAMES.ANSWER_GENERATION_STREAMED,
                  {
                    customParams: {
                      source: FEDOPS.ANSWER_GENERATION_SOURCES.CACHE,
                    },
                  }
                );
                isFedopsInteractionEndedRef.current = true;
              }
              const answerText = partial.generatedAnswer.answer
                ? JSON.parse(partial.generatedAnswer.answer).solution
                : '';

              finishStreaming();
              setText(answerText);
              setIsProhibited(partial.generatedAnswer.prohibited);
              setUsedArticlesInfo(
                partial.generatedAnswer.metadata.usedArticlesInfo
              );
              const media = partial.generatedAnswer.metadata.mediaRefsMap || {};
              setMediaRefsMap(media);
              void sendSearchQuestionBI(
                answerText,
                Object.keys(partial.generatedAnswer.metadata.usedArticlesInfo),
                partial.generatedAnswer.prohibited,
                media,
                {
                  inputText: newTerm,
                  results: effectiveResults?.items as ArticleSearchResultItem[],
                  searchMethod: effectiveResults?.searchMethod,
                  searchVersion: effectiveResults?.searchVersion,
                  inputType: biInputType,
                }
              );
            }
          },
          abortControllerRef.current
        );
        finishStreaming();
        return { completed: true, aborted: false };
      } catch (error) {
        const isAborted = error instanceof Error && error.name === 'AbortError';
        resetAnimation(true);
        setUsedArticlesInfo({});
        setMediaRefsMap({});
        await sendSearchQuestionBI('', undefined, undefined, undefined, {
          inputText: newTerm,
          results: effectiveResults?.items as ArticleSearchResultItem[],
          searchMethod: effectiveResults?.searchMethod,
          searchVersion: effectiveResults?.searchVersion,
        });
        return { completed: false, aborted: isAborted };
      }
    }
    return { completed: false, aborted: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fedopsApi,
    httpClient,
    isContactSupport,
    sendSearchQuestionBI,
    term,
    searchTerm,
    searchSessionId,
    currentSearchResults]);

  useEffect(() => {
    void (async () => {
      setIsLoading(true);
      const { aborted, completed } = await generateAnswer(term, false, searchResults);
      if (!aborted) {
        setIsLoading(false);
        if (!completed && searchResults?.items?.length === 0) {
          await sendSearchQuestionBI(undefined, undefined, undefined, undefined, {
            inputText: term,
            results: [],
            searchMethod: searchResults?.searchMethod,
            searchVersion: searchResults?.searchVersion,
          });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  useEffect(() => {
    if (!shouldGenerateAnswer) {
      void sendSearchQuestionBI(undefined, undefined, undefined, undefined, {
        inputText: term,
        results: searchResults?.items as ArticleSearchResultItem[],
        searchMethod: searchResults?.searchMethod,
        searchVersion: searchResults?.searchVersion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldGenerateAnswer, term]);

  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    []
  );

  const onSearch = async (newTerm: string, inputType?: string) => {
    abortControllerRef.current?.abort();
    setIsLoading(true);
    setIsContactSupportState(false);
    setRefinementQuestions([]);

    try {
      resetAnimation(false);
      setSearchTerm(newTerm);
      const inputTypeParam = inputType
        ? `&inputType=${encodeURIComponent(inputType)}`
        : '';
      const searchUrl =
        `${locale}/search?term=${newTerm}&editorType=${editorType}` +
        `&previousPageType=${previousPageType}${inputTypeParam}`;
      await pushUrl(searchUrl, true);
      setCurrentSearchResults({
        ...currentSearchResults,
        isLoading: true,
      });
      const newSearchResults = await fetchSearchResults(1, newTerm);
      setIsContactSupportState(newSearchResults.isContactSupport || false);
      setCurrentSearchResults({ ...newSearchResults, isLoading: false });
      if (newSearchResults.isContactSupport) {
        setIsLoading(false);
        return;
      }
      if (newSearchResults.items.length > 0) {
        const { aborted } = await generateAnswer(newTerm, true, newSearchResults);
        if (!aborted) {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
        await sendSearchQuestionBI(undefined, undefined, undefined, undefined, {
          inputText: newTerm,
          results: [],
          searchMethod: newSearchResults?.searchMethod,
          searchVersion: newSearchResults?.searchVersion,
        });
      }
    } catch (error) {
      console.error('error', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);

    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  const retryGenerateAnswer = async () => {
    setIsLoading(true);
    const { aborted } = await generateAnswer(searchTerm, false, currentSearchResults);
    if (!aborted) {
      setIsLoading(false);
    }
  };

  const hasResults =
    term.length > 0 &&
    currentSearchResults &&
    initNumberOfResults &&
    (initNumberOfResults > 0 || isLoading);

  const limitedSearchResults = {
    ...currentSearchResults,
    itemsCount: Math.min(
      currentSearchResults.itemsCount,
      ELASTIC_SEARCH_LIMIT
    ),
  };

  return (
    <div ref={searchContainerRef}>
      <SearchResults
        isLoading={isLoading}
        searchTerm={searchTerm}
        searchResults={limitedSearchResults}
        isContactSupport={isContactSupportState}
        categoriesPaths={allPaths}
        locale={locale as string}
        generatedAnswer={generatedAnswer}
        isProhibited={isProhibited}
        isStreaming={isAnimating}
        usedArticlesInfo={usedArticlesInfo}
        mediaRefsMap={mediaRefsMap}
        retryGenerateAnswer={retryGenerateAnswer}
        onSearch={onSearch}
        refinementQuestions={refinementQuestions}
        showNoResults={!hasResults}
      />
    </div>
  );
};

export const getServerSideProps: GetWixServerSidePropsFunction =
  withServerSideProps(async ({ req, res, locale, query }) => {
    const logger = createLogger(LoggerNamespaces.Server);
    const {
      term,
      page = 1,
      previousPageType,
      inputType = '',
      editorType,
    } = query;
    const editor = editorType
      ? metaSiteSearchEditorTypeConverter(editorType as EditorType)
      : null;
    let sanitizedTerm = '',
      pageNumber = 1;
    try {
      sanitizedTerm = DOMPurify.sanitize(decodeURIComponent(term as string), {
        ALLOWED_TAGS: [],
      });
      pageNumber = !isNaN(Number(page)) ? Number(page) : 1;
    } catch (error: unknown) {
      logger
        .withAspects(req.aspects)
        .error('Error decoding term or page', term, page, error);
      const message = error instanceof Error ? error.message : String(error);
      res.status(HttpStatusCode.BAD_REQUEST).send(message);
    }
    const { searchResults = [], isContactSupport = false } =
      await getKASearchResults(
        req.aspects,
        res,
        sanitizedTerm,
        pageNumber,
        locale,
        editor
      );

    const pageSeo = await getPageSeo(
      req.aspects,
      locale,
      PageType.SearchResults,
      res.locals.redisClient
    );

    return {
      props: {
        term: sanitizedTerm,
        pageType: PageType.SearchResults,
        pageData: { searchResults, term: sanitizedTerm },
        searchResults: { ...searchResults, isLoading: false },
        initCurrentPage: pageNumber,
        itemId: itemIdKey(sanitizedTerm, pageNumber),
        pageSeo,
        isContactSupport,
        biInputType: inputType,
        editor,
        previousPageType: !isNaN(Number(previousPageType))
          ? Number(previousPageType)
          : null,
      },
    };
  });

export default SearchPage;
